import { type ChangeEvent, useCallback, useState, useRef } from 'react';
import { useDownloadFile } from '@/hooks/useDownloadFile';
import { notifications } from '@mantine/notifications';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { EXPORT_FILETYPES } from '@/app/constants';
import { useExportQuery } from '@/hooks/useExportQuery';
import { Stack, Text, Paper, Box, Radio } from '@mantine/core';
import Modal from '@/components/Modals/Modal';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import type { MessageId } from '@/assets/lang/en-US';

import classes from './ExportModal.module.css';

interface Props {
  /**
   * Optional label for export component
   */
  title?: string;
  showExportModal?: boolean;
  onClose?: () => void;
  /**
   * Tracking suffix for analytics events (e.g., 'analysis', 'projects')
   */
  trackingSuffix?: string;
  /**
   * Translation key for the entity being exported (e.g., 'noun.spans', 'noun.project')
   */
  exportEntityTranslationKey?: MessageId;
  /**
   * Custom export endpoint path. If not provided, defaults to analysis export endpoint.
   * Should include the full path starting with /v1/
   */
  exportEndpoint?: string;
  /**
   * Whether to use GET method instead of POST (defaults to false)
   */
  useGetMethod?: boolean;
}

const useExportFormat = () => {
  const intl = useClientTerminology();

  const exportOptions = [
    {
      value: EXPORT_FILETYPES.csv,
      label: intl.formatMessage({ id: 'export.fileTypeCSV' }),
    },
    {
      value: EXPORT_FILETYPES.xls,
      label: intl.formatMessage({ id: 'export.fileTypeXLS' }),
    },
  ];

  const [selectedFormat, setSelectedFormat] = useState(exportOptions[0].value);

  const handleFormatChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedFormat(event.target.value);
    },
    []
  );

  return {
    exportOptions,
    selectedFormat,
    handleFormatChange,
  };
};

const useExportAnalytics = (trackingSuffix?: string) => {
  const trackExportEvent = useCallback(
    (event: string) => {
      if (trackingSuffix) {
        TagManager.dataLayer({
          dataLayer: {
            event: `${event}_${trackingSuffix}`,
          },
        });
      }
    },
    [trackingSuffix]
  );

  const trackExportAttempt = useCallback(
    (format: string) => {
      const event =
        format === EXPORT_FILETYPES.csv
          ? GoogleEvents.exportCSV
          : GoogleEvents.exportXLS;
      trackExportEvent(event);
    },
    [trackExportEvent]
  );

  return {
    trackExportEvent,
    trackExportAttempt,
  };
};

export const ExportModal = ({
  title,
  showExportModal,
  onClose,
  trackingSuffix,
  exportEntityTranslationKey = 'noun.spans',
  exportEndpoint,
}: Props) => {
  const downloadFileQuery = useExportQuery({
    exportEndpoint,
  });
  const intl = useClientTerminology();
  const currentRequestRef = useRef<{ cancel: () => void } | null>(null);
  const [loading, setLoading] = useState(false);

  const { exportOptions, selectedFormat, handleFormatChange } =
    useExportFormat();
  const { trackExportEvent, trackExportAttempt } =
    useExportAnalytics(trackingSuffix);

  const handleCloseModal = useCallback(() => {
    currentRequestRef.current?.cancel();
    onClose?.();
  }, [onClose]);

  const handleError = useCallback(
    (error: Response | Error) => {
      trackExportEvent(GoogleEvents.exportFailed);

      const errorMessage = intl.formatMessage({
        id:
          error instanceof Response && error.status === 413
            ? 'export.fileTooLarge'
            : 'export.fileErrorMessage',
      });

      notifications.show({
        title: intl.formatMessage({ id: 'action.error' }),
        message: errorMessage,
        color: 'red',
      });
    },
    [trackExportEvent, intl]
  );

  const handleSuccess = useCallback(() => {
    trackExportEvent(GoogleEvents.exportSuccessful);
    onClose?.();
  }, [trackExportEvent, onClose]);

  const { download } = useDownloadFile({
    postApi: () => {
      const request = downloadFileQuery(selectedFormat);
      currentRequestRef.current = request;
      return request.getExport;
    },
    onError: handleError,
    onLoading: setLoading,
    onSuccess: handleSuccess,
  });

  const handleExportClick = useCallback(() => {
    download();
    trackExportAttempt(selectedFormat);
  }, [download, selectedFormat, trackExportAttempt]);

  return (
    <Modal
      opened={showExportModal!}
      title={title}
      onClose={handleCloseModal}
      closeOnClickOutside={true}
      onPrimaryAction={handleExportClick}
      isLoading={loading}
      primaryLabel={intl.formatMessage(
        { id: 'action.export' },
        {
          thing: intl.formatMessage({ id: exportEntityTranslationKey }),
        }
      )}
      size="lg"
      testId="export-modal"
      primaryButtonTestId="export-modal-primary-button"
    >
      <Stack gap="md" p="md">
        <Text fw={500}>
          {intl.formatMessage({
            id: 'export.optionsTitle',
          })}
        </Text>
        <Paper withBorder radius="md">
          {exportOptions.map((option, key) => {
            return (
              <Box key={key} p="md">
                <Radio
                  value={option.value}
                  label={option.label}
                  checked={selectedFormat === option.value}
                  onChange={handleFormatChange}
                  data-testid={`export-format-${option.value}`}
                  labelPosition="left"
                  variant="outline"
                  classNames={{
                    labelWrapper: classes.labelWrapper,
                    label: classes.label,
                    icon: classes.icon,
                    radio: classes.radio,
                  }}
                />
              </Box>
            );
          })}
        </Paper>
      </Stack>
    </Modal>
  );
};
